import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";

import "../styles/blog.css";
import MouseFooter from "../components/MouseFooter";
import BackgroundCanvas from "../components/BackgroundCanvas";

/* ── Articles ──────────────────────────────────────────────────────────────
   Each article: title, hero image, and a list of sections that alternate
   explanatory text with pictures so readers see how each piece works.        */
const ARTICLES = [
  {
    slug: "egoc-egusd",
    tag: "Tokenomics",
    title: "EGOC & EGUSD: Two Tokens, Two Jobs",
    date: "July 2026",
    excerpt:
      "EGOC secures and powers the network; EGUSD is the native stable dollar you actually pay with. How the two work together — and why usage makes EGOC scarcer.",
    hero: "/Coins/ego_square.png",
    heroVariant: "logo",
    sections: [
      { type: "text", body: "The Ego network runs on two native units that do different work. EGOC is the network coin — it secures the chain and rewards the people who power it. EGUSD is Ego's native stablecoin — 1 EGUSD = 1 US dollar, always — and it's what you actually pay with. Separating the two is what lets Ego be both an investment-grade network asset and a currency you can price a house in." },
      { type: "image", src: "/Coins/ego_square.png", variant: "logo", caption: "EGOC — the floating network coin that secures and powers Ego." },
      { type: "text", heading: "EGOC — the network coin", body: "EGOC is earned, staked, and traded freely. Node operators earn it for the real work their machines do — validating blocks, storing files, providing network coverage, renting out compute. Validators stake it as collateral for honest behavior. Its price floats on the open market, and that's by design: EGOC is the share of the network's growth. As the network gets used, EGOC becomes scarcer — the upside belongs to the people running and holding it." },
      { type: "image", src: "/Coins/egusd.png", variant: "logo", caption: "EGUSD — Ego's native stablecoin. 1 EGUSD = 1 US dollar, always." },
      { type: "text", heading: "EGUSD — the native stable dollar", body: "EGUSD doesn't float, doesn't speculate, and doesn't surprise you. It exists for one purpose: real payments — rent and real-estate settlements, government taxes and fees, invoices, storage and compute bills. A payment of 1,250 EGUSD is worth $1,250 the day you agree it and the day it settles, no matter what the crypto market did in between. And it's a first-class asset of the chain itself: every balance and every transfer is validated by every node — no bank, no issuing company, no IOU." },
      { type: "text", heading: "How EGUSD is created: burn, don't borrow", body: "EGUSD enters circulation one way: by burning EGOC. Send EGOC to the burn address and the protocol mints EGUSD at the live oracle price, verified independently by every validator. If EGOC trades at $4, burning 25 EGOC mints 100 EGUSD; if EGOC later trades at $8, the same 100 EGUSD costs only 12.5 EGOC. Minting is strictly one-way — EGUSD can never be converted back into freshly minted EGOC. That single rule is what makes the design safe: there is no reflexive loop and no algorithmic peg to defend. EGUSD is not a promise about EGOC's price; it's a receipt for EGOC that was permanently destroyed. Want EGOC again? Buy it on the market like anyone else — the protocol never prints it for you." },
      { type: "image", src: "/Coins/buy-egusd.png", caption: "Converting EGOC to EGUSD inside the Ego Desktop wallet — one click, at the live network price." },
      { type: "text", heading: "Getting EGUSD in Ego Desktop", body: "Open the Wallet, press the $ EGUSD button, and enter how much EGOC you want to convert. The wallet shows you the exact EGUSD you'll receive at the live network price before you confirm — convert 10 EGOC at $2.45 and 24.50 EGUSD lands in your balance a block later. The conversion is a real on-chain transaction: the EGOC is burned, the mint is checked by every validator against the network's price oracle, and your EGUSD balance lives in the chain's state on every node — not in some company's database." },
      { type: "text", heading: "What you can do with EGUSD today", body: "Send it to anyone: EGUSD transfers to any Ego address in seconds, with the network rejecting overdrafts by consensus — perfect for invoices, rent, or paying a friend back an exact dollar amount. Rent compute with it: when you book GPU or CPU power on the marketplace, choose 'Pay with EGUSD' and the full rental price locks in dollars at booking — no matter what EGOC does during a three-day training run, your cost doesn't move, and the provider earns stable income per period with the same automatic escrow protection. Storage deals and website hosting are joining the same rails next, and dApps will be able to price in EGUSD too — so an app on Ego can bill exactly like a real business." },
      { type: "text", heading: "Why this helps EGOC holders", body: "Every dollar of real-world usage on Ego — every tax bill, every property settlement, every compute rental paid in EGUSD — required someone to burn EGOC first. Adoption doesn't just raise demand for EGOC; it permanently shrinks its supply. The more the network is used as money, the scarcer the network coin becomes. Usage is the buy-and-burn." },
      { type: "text", heading: "Why we didn't peg EGOC itself", body: "Some projects promise their coin will stay inside a price band. History is blunt about how that ends: a ceiling is easy to defend, but a floor must be bought with real reserves — and every algorithmic floor that ran out of reserves collapsed, taking its users' savings with it. Ego promises only what the protocol mechanically enforces. The unit that must be stable — what you pay with — is stable by construction; the asset that should be free — the network's value — stays free. Institutions get a dollar-exact, auditable settlement unit on a quantum-resistant chain; the network keeps an honest, market-priced coin. Price it in EGUSD. Get paid in certainty. Power it all with EGOC." },
    ],
  },
  {
    slug: "wallet",
    tag: "Wallet",
    title: "The Ego Wallet: Self-Custody, Quantum-Safe",
    date: "June 2026",
    excerpt:
      "Hold your own keys, send and receive EGOC, swap assets, and keep transfers out of public view in Ego apps.",
    hero: "/Wallet/wallet.png",
    sections: [
      { type: "text", body: "The Ego Wallet is your home base on the network. There are no accounts to sign up for and no company holding your funds — you control your own keys, protected by post-quantum cryptography. Your balance, public keys, and history all live with you." },
      { type: "text", heading: "Sending and receiving", body: "Send EGOC to any address in seconds. The wallet builds the transaction, signs it with your quantum-safe key, and broadcasts it to the network for the validators to finalize." },
      { type: "image", src: "/Wallet/send.png", caption: "Sending EGOC — enter an address and amount, and the wallet handles signing and broadcast." },
      { type: "text", heading: "Keeping a transfer out of view", body: "A hidden send removes the amount and the counterparties from Ego Desktop and the Ego Explorer, so a payment is not surfaced to anyone browsing normally. It is not encryption: the details are still written to the chain and remain readable by anyone who looks there directly, so do not rely on it where being identified would put you at risk." },
      { type: "image", src: "/Wallet/shielded-send.png", caption: "A hidden send keeps a transfer out of view in Ego apps." },
      { type: "text", heading: "Swapping assets", body: "Swap between assets directly from the wallet, with live pricing — no third-party exchange holding your coins in the middle." },
      { type: "image", src: "/Wallet/swap.png", caption: "Swap assets in-wallet with live rates." },
      { type: "text", heading: "Joining the presale", body: "Early supporters can reserve EGOC during the presale and claim it at genesis, paying with crypto right from the wallet." },
      { type: "image", src: "/Wallet/presale-card.png", caption: "Reserve your allocation during the presale and pay with crypto." },
      { type: "text", heading: "Claiming your EGOC", body: "When it's time, claiming is simple: open your presale receipt in the wallet and confirm. Your reserved EGOC is credited to your address, ready to send, stake, or swap." },
      { type: "image", src: "/Wallet/claim-presale.png", caption: "Claim your reserved EGOC — confirm in the wallet and it lands in your balance." },
    ],
  },
  {
    slug: "stake",
    tag: "Staking",
    title: "Staking: Secure the Network and Earn",
    date: "June 2026",
    excerpt:
      "Lock EGOC to back the validators, strengthen consensus, and earn a steady return.",
    hero: "/Stake/stake.png",
    sections: [
      { type: "text", body: "Staking is how you put your EGOC to work securing Ego. By locking coins as stake, you give weight to honest validators and make attacking the network economically painful." },
      { type: "text", heading: "How it works", body: "You choose an amount to stake and a lock period. Staked coins back the Byzantine-fault-tolerant consensus that finalizes blocks. The longer and larger your commitment, the more you support the chain — and the more you earn in return." },
      { type: "text", heading: "Rewards and unstaking", body: "Rewards accrue while your stake is active. You can unstake when your lock period ends; the wallet shows your projected interest and any early-unstake terms up front, so there are no surprises." },
    ],
  },
  {
    slug: "earn",
    tag: "Earn",
    title: "Earn: Get Paid for Running a Node",
    date: "June 2026",
    excerpt:
      "Coverage, storage, and retrieval all pay. Keep your node online and watch the rewards add up.",
    hero: "/Earn/earn.png",
    sections: [
      { type: "text", body: "Every Ego node is also a worker. Instead of mining empty puzzles, your machine earns by doing useful things for the network: proving coverage, holding other people's data, and serving files when they are requested." },
      { type: "image", src: "/Earn/earn-module.png", caption: "The Earn module tracks your live session and the rewards you are accruing." },

      { type: "text", heading: "The four ways a node earns", body: "Rewards are set in US dollars and converted to EGOC at the live oracle price, so what you earn stays steady whether EGOC is worth a cent or ten dollars. Coverage pays $0.15 a day for proving there is real wireless signal at your location. Consensus pays $0.20 a day if you are a registered, staked validator taking part in block production. Storage pays $0.002 per gigabyte per day for peer data you are actually holding. Retrieval pays $0.003 per gigabyte you serve back when someone requests their files." },

      { type: "text", heading: "Storage pays for data held, not space offered", body: "This is the part people get wrong most often. Allocating 500 GB does not earn 500 GB of rewards. You are paid for peer data actually stored on your machine, capped by what you pledged. A node with 500 GB allocated and 20 GB hosted is paid for 20 GB. Allocation is a promise of capacity; payment follows the data that arrives. Early on, when few files are circulating, storage earnings will be small no matter how much space you set aside." },

      { type: "text", heading: "Uptime is the single biggest lever", body: "Coverage and consensus pay per day, and both require your node to be running. Closing the app stops the clock. A node online 24 hours a day earns roughly three times what a node online eight hours a day earns, from exactly the same hardware. Ego Desktop is built for this: it starts with your computer and runs in the background, so you do not have to remember to open it." },

      { type: "text", heading: "Your DRS score decides your share", body: "Deterministic Reward Scoring measures what you actually contribute. It combines your coverage proofs, weighted at 60 percent, with your storage proofs, weighted at 40 percent. You need a combined score of at least 0.5 to be eligible for block production at all. Coverage score builds from beacons submitted over a rolling 24 hours, so a node that has been online continuously scores far higher than one that reconnects occasionally." },

      { type: "text", heading: "How to earn more, in order of impact", body: "Keep the node running continuously, because uptime compounds across every reward type. Turn on coverage, since it is the largest single daily payment and needs no disk space. Allocate storage you can genuinely leave in place, because reducing it later carries a penalty. Register and stake as a validator to unlock consensus rewards, as an unstaked validator vote does not count toward quorum. And be early: rewards carry a bootstrap multiplier that is highest for the first hundred nodes and decays as the network grows." },

      { type: "text", heading: "Things that cost you rewards", body: "Reducing your storage allocation after committing suspends rewards for a period and records a strike against your node. Going offline stops coverage and consensus payments immediately. And rewards taper as the node pool is drawn down, paying the full rate until the pool falls below a fifth remaining and scaling down after that. None of these are punishments for leaving; they exist because other people are relying on the capacity you pledged." },

      { type: "text", heading: "A note on testnet", body: "Ego is on public testnet. Everything described here runs today and you can watch it accrue in the Earn module, but testnet EGOC is not tradeable and has no monetary value. Mainnet follows an independent security audit. The rates above are what the protocol pays; what that is worth in real money is a question for after launch." },
    ],
  },
  {
    slug: "5g-device",
    tag: "5G Devices · Soon",
    title: "The Ego 5G Device: The Always-On End Goal",
    date: "June 2026",
    excerpt:
      "Purpose-built hardware that runs an Ego node 24/7 — so you contribute coverage and storage around the clock without leaving your computer on. This is where Ego is headed.",
    hero: "/egodevice.png",
    sections: [
      { type: "text", body: "Running Ego on your laptop or desktop already makes you part of the network — but to keep earning, the app has to stay open and your machine has to stay on. The Ego 5G Device is the answer to that, and it's the end goal of the whole project: a small, dedicated, always-on node that contributes coverage and storage every hour of every day, so you're always active and always earning — without tying up your computer." },
      { type: "image", src: "/egodevice.png", caption: "The Ego 5G Device — a portable, carrier-compliant node built to run Ego natively, 24/7." },
      { type: "text", heading: "Always on, always earning", body: "Because it's purpose-built, low-power hardware, you simply leave it running like a Wi-Fi router. It proves coverage and serves storage continuously, so your rewards never pause when you close your laptop or shut down your PC. That's the difference between participating when you remember to, and participating all the time." },
      { type: "text", heading: "What it does", body: "The device runs the same jobs your desktop node does, but natively and non-stop. Proof of Coverage: it listens to live 5G signals and publishes cryptographically signed coverage evidence. Verifiable storage: it holds encrypted data and answers randomized audits to prove it's still available. Self-healing replication: it keeps multiple independent copies online, repairing automatically if one drops. And smart networking: it prefers Wi-Fi or Ethernet for heavy uploads while keeping cellular data lean." },
      { type: "text", heading: "Built for the real world", body: "It's post-quantum secure by default, and its coverage proofs are operator-compliant on licensed 5G — no unauthorized transmissions. It's portable, silent, and runs on battery or 12V, so it works at home, in an office, or on the move. Setup takes minutes: insert a SIM/eSIM, power it on, link your Ego account, and follow the wizard." },
      { type: "text", heading: "Why it's the end goal", body: "The desktop app proves the model works; the device makes it effortless and continuous. It's how Ego becomes a truly people-powered network that's always on — extending real coverage and storage everywhere its operators are. It's launching to early operators through an invite-only pilot, rolling out in phases with signed over-the-air updates. Coming soon — and worth the wait." },
    ],
  },
  {
    slug: "market",
    tag: "Market",
    title: "The Market: Live Prices at a Glance",
    date: "June 2026",
    excerpt:
      "Track EGOC and other assets with live prices and charts, right inside the app.",
    hero: "/Market/market.png",
    sections: [
      { type: "text", body: "The Market page brings live pricing into Ego Desktop so you can follow the value of your assets without leaving the app." },
      { type: "image", src: "/Market/prices.png", caption: "Live prices for EGOC and tracked assets." },
      { type: "text", heading: "Reading the charts", body: "Candlestick charts show how price moves over time, so you can spot trends before you send, swap, or stake." },
      { type: "image", src: "/Market/candles.png", caption: "Candlestick charts reveal momentum and trends at a glance." },
    ],
  },
  {
    slug: "storage",
    tag: "Storage",
    title: "Storage: Rent Out Your Disk Space",
    date: "June 2026",
    excerpt:
      "Pledge unused disk space, pass random audits, and earn for keeping the network's data safe.",
    hero: "/Storage/storage.png",
    sections: [
      { type: "text", body: "Ego turns your spare disk space into income. You decide how much to provide, and the network fills it with encrypted data it needs to keep durable and available." },
      { type: "image", src: "/Storage/Configurate-storage-provision.png", caption: "Configure how much storage you want to provision to the network." },
      { type: "text", heading: "Proving you still hold it", body: "Storing data isn't enough — the network must know it stays available. Through Proof of Spacetime, your node is challenged at random to prove it still holds each file intact. Pass and you earn; if a copy ever goes missing, the network repairs it elsewhere automatically." },
      { type: "image", src: "/Storage/Storage-package.png", caption: "Storage packages define the capacity and terms you offer." },
    ],
  },
  {
    slug: "egosafe",
    tag: "EgoSafe",
    title: "EgoSafe: Encrypt and Share Files Privately",
    date: "June 2026",
    excerpt:
      "Files encrypted on your device before they ever leave it — share a secure link only the recipient can open.",
    hero: "/EgoSafe/egoSafe.png",
    sections: [
      { type: "text", body: "EgoSafe lets you store and share files without trusting anyone with their contents. Each file is encrypted on your own device with AES-256-GCM before it's uploaded, so the nodes holding it only ever see ciphertext." },
      { type: "text", heading: "How sharing works", body: "When you share, EgoSafe creates a single secure bundle containing the file's location and the key needed to unlock it. Only someone you give that bundle to can decrypt the file — the network itself never can." },
      { type: "text", heading: "Private by construction", body: "Because encryption happens before upload and the keys stay with you, your data is confidential by design. Combined with replication, you get private storage that's also durable and always available." },
    ],
  },
  {
    slug: "coverage",
    tag: "Coverage",
    title: "Proof of Coverage: Mapping the Real World",
    date: "June 2026",
    excerpt:
      "How Ego turns wireless signals into cryptographic proof — and rewards the people extending connectivity.",
    hero: "/Coverage/coverage.png",
    sections: [
      { type: "text", body: "Proof of Coverage is how Ego verifies that a node is genuinely present and providing connectivity in a real, physical place — rather than trusting a self-reported location." },
      { type: "text", heading: "How the proof is built", body: "A node captures radio metrics and a precise time and location reference, then signs that reading with its post-quantum key. Neighboring nodes corroborate it. Because every reading is signed, time-stamped, and cross-checked, a node can't claim to be somewhere it isn't." },
      { type: "text", heading: "Why coverage earns rewards", body: "Strong, well-connected coverage raises your DRS weight, which increases your chance of winning a block slot through the VRF lottery. The more useful your connectivity is to the network, the more you earn — coverage becomes a rewarded contribution instead of an invisible cost." },
    ],
  },
  {
    slug: "hosting",
    tag: "Hosting",
    title: "Hosting: Put Websites on the Network",
    date: "June 2026",
    excerpt:
      "Host real websites across Ego nodes — censorship-resistant, always-on, and served from everywhere.",
    hero: "/Hosting/hosting.png",
    sections: [
      { type: "text", body: "Ego Hosting lets you publish a website that lives on the decentralized network instead of a single server. Your content is distributed across nodes, so there's no single point to take it down." },
      { type: "image", src: "/Hosting/preview-website.png", caption: "Preview your site before publishing it to the network." },
      { type: "text", heading: "How it serves", body: "Once published, requests for your site are answered by Ego nodes that hold its files. Hosts earn for serving content, and visitors get a fast, resilient site with no central host to fail or censor it." },
    ],
  },
  {
    slug: "compute",
    tag: "Compute",
    title: "Compute: Rent Power, Run AI",
    date: "June 2026",
    excerpt:
      "Tap the network's spare compute to run and train AI models — isolated, on-demand, and pay-as-you-go.",
    hero: "/Compute/run-ai.png",
    sections: [
      { type: "text", body: "Ego Compute turns idle machines into an on-demand cloud. Renters get power when they need it; providers earn for sharing theirs. Each rental runs in its own isolated sandbox so workloads never interfere." },
      { type: "image", src: "/Compute/detect.png", caption: "Run inference tasks like detection on rented compute." },
      { type: "text", heading: "Training for companies", body: "Beyond quick inference, teams can train models on the network's compute, scaling across providers without standing up their own data center." },
      { type: "image", src: "/Compute/train (for companies).png", caption: "Train models at scale using the network's shared compute." },
    ],
  },
  {
    slug: "contracts",
    tag: "Smart Contracts",
    title: "Smart Contracts: Program the Chain",
    date: "June 2026",
    excerpt:
      "Deploy contracts, call their functions, and build on-chain logic that runs exactly as written.",
    hero: "/Contracts/contract.png",
    sections: [
      { type: "text", body: "Smart contracts let you put rules on-chain that execute automatically and can't be quietly changed. From tokens to escrows to governance, they're the building blocks of decentralized apps on Ego." },
      { type: "image", src: "/Contracts/deploy.png", caption: "Deploy a contract to the network in a few clicks." },
      { type: "text", heading: "Interacting with a contract", body: "Once deployed, you can call a contract's functions directly — read its state or trigger actions — and every call is recorded on-chain." },
      { type: "image", src: "/Contracts/interact.png", caption: "Call functions and read state from a deployed contract." },
      { type: "text", heading: "What you can build", body: "Tokens, marketplaces, DAOs, payment flows, and more — anything that benefits from rules no single party can override." },
      { type: "image", src: "/Contracts/what-you-can-build.png", caption: "A glimpse of what's possible with Ego smart contracts." },
    ],
  },
  {
    slug: "dapp",
    tag: "dApps",
    title: "The dApp IDE: Build and Ship On-Chain",
    date: "June 2026",
    excerpt:
      "Write, test, and deploy decentralized apps from a built-in IDE — no separate toolchain required.",
    hero: "/dApp/dapp.png",
    sections: [
      { type: "text", body: "The dApp IDE brings development right into Ego Desktop. Write your app, connect it to your contracts, and ship it to the network without juggling external tools." },
      { type: "image", src: "/dApp/deploy.png", caption: "Deploy your dApp straight from the built-in IDE." },
      { type: "text", heading: "From idea to live app", body: "Build the interface, wire it to on-chain logic, and deploy — your dApp runs on the same decentralized infrastructure that powers the rest of Ego." },
    ],
  },
  {
    slug: "offline-send",
    tag: "Offline",
    title: "Paying Without the Internet",
    date: "September 2026",
    excerpt:
      "Your WiFi is fine. It is the internet behind it that is gone. Ego can still get a payment out of your machine and into the hands of every other node in the room, with nothing to configure.",
    hero: "/Radio/radio.png",
    sections: [
      { type: "text", body: "A signed transaction is a self-contained object. It names the sender, the recipient, the amount and a nonce, and it carries the signature proving the sender authorised exactly that. Any node can check it without asking anyone anything. So the internet is not what makes a payment valid — it is only how the payment usually travels. Take it away and the transaction is still perfectly good. It just needs another ride." },
      { type: "image", src: "/Radio/radio.png", caption: "Switch the offline link on and the payment goes out over the local network instead of the internet." },
      { type: "text", heading: "The outage people actually have", body: "Your ISP is out, or you are on a hotspot with the data allowance spent, or you are on a plane’s WiFi, or a conference network that has collapsed. The machines in the room can still reach each other perfectly. Your phone shows WiFi with no internet. That is when this earns its place." },
      { type: "text", heading: "Why it needs no setup", body: "There is no address to know, no peer to discover, no handshake to time out. You shout onto the wire and anyone listening on that port hears it. The only bookkeeping is an eight-byte tag on the front so a node skips the echo of its own broadcast. Nothing trusts that tag — the transaction inside carries the signature that decides whether it is real, which is the whole reason this can work with strangers." },
      { type: "text", heading: "Getting to the chain", body: "The offline link does not put your payment on the chain. It gets it out of your machine and into other nodes’ hands, and whichever of them has a connection first is what carries it onward. If nobody in earshot ever reconnects, it waits — and the panel says so, counting the frames nothing has collected yet. That number is the one to watch, because an offline send looks identical to an ordinary one at the moment you press the button and only differs later." },
      { type: "text", heading: "Built for a link that drops things", body: "The payment is cut into small frames, two hundred bytes each, because the point is to survive a narrow and unreliable path. A node that ends up holding frames one, two and four asks for three specifically rather than making you send it all again, and keeps trying for a while before giving up. There is a rate limit so a noisy sender cannot flood the link. None of that is visible when it works. It is what makes it work at all." },
      { type: "text", heading: "When there is no network at all", body: "The same frames can be written to a folder instead. Every Ego process on one machine already shares that folder, so nodes on the same computer find each other with nothing set up whatsoever. Beyond one machine you move it yourself — a shared drive, or a USB stick you walk across a building. That is the floor rather than the feature, but it means the system degrades to something rather than to nothing." },
      { type: "text", heading: "Where the radio comes in", body: "There is no radio in this build, and the badge is the direction rather than a claim. The framing layer above is deliberately transport-agnostic, so a radio is just another link plugged into it and none of the rest has to change. The honest obstacle is size: a post-quantum signature is large, which makes an Ego transaction around eight kilobytes, while European rules cap an 868 MHz device at thirty-six seconds of transmission per hour. That is a handful of payments an hour at best. Sending frames as binary rather than hexadecimal roughly halves a transaction and is the work that has to come first — then a radio is worth fitting, for the case it is genuinely for: a remote valley, a disaster zone, somewhere with no tower at all." },
      { type: "text", heading: "This payment is still public", body: "An offline send is an ordinary transaction that took an unusual route. Your address, the recipient and the amount land on the chain exactly as they would have, readable by anyone. Sending without the internet is not the same as sending without an audience. If you want the second, use the shielded pool, which hides who paid whom whether you are online or not." },
    ],
  },
  {
    slug: "shielded",
    tag: "Privacy",
    title: "Shielded Transactions: Spending Without an Audience",
    date: "September 2026",
    excerpt:
      "On most blockchains your salary, your rent and your savings are readable by anyone who learns one address. Ego's shielded pool breaks that link with maths rather than hiding it in the interface.",
    hero: "/Shield/shield.png",
    sections: [
      { type: "text", body: "A public ledger is a strange thing to be paid into. Give someone your address so they can send you ten coins, and you have also given them every payment you have ever received, every balance you have held, and every address you have ever paid. Nobody would accept that from a bank. Ego's shielded pool is the part of the wallet that takes it back." },
      { type: "image", src: "/Shield/shield.png", caption: "The shielded pool before anything is in it. Shielding 200 EGOC will produce two notes of 100." },
      { type: "text", heading: "Two steps, and no thread between them", body: "You shield coins into the pool, which is a public transaction: everyone can see you put funds in. Later you send from the pool to any address. That second transaction has the pool's own address as its sender, identical on every withdrawal ever made, and carries a mathematical proof instead of a signature. An observer sees a deposit, and separately a payment, and has nothing on the chain that ties one to the other." },
      { type: "text", heading: "Why the amounts are round numbers", body: "Notes come in fixed sizes: 1, 10, 100, 1,000 and 10,000 EGOC. That looks like a limitation and is actually the point. Amounts have to stay public, or nodes could not check that the pool balances and nobody could tell whether coins were being invented. So if you could shield 147.32 EGOC, the amount alone would identify you the moment it came out again. Identical sizes are what stop the number being a name. One withdrawal spends up to sixteen notes, so 350 EGOC is a single payment of three hundreds and five tens." },
      { type: "image", src: "/Shield/shield2.png", caption: "After shielding: 200 EGOC sitting in the pool as two notes, ready to send anywhere." },
      { type: "text", heading: "Proofs with nothing to trust", body: "The proof is a STARK — hash-based, which matters for two reasons. There is no trusted setup: no ceremony, no secret parameters that could have been kept by whoever ran it, nothing that has to have been destroyed honestly years ago for your privacy to hold today. And being hash-based it stays quantum-safe, like the rest of Ego's cryptography, rather than resting on elliptic curves a quantum computer would eventually unpick. Spending a note publishes a nullifier, a one-way fingerprint the chain records and refuses a second time — that is what stops a note being spent twice, while revealing nothing about which deposit it came from." },
      { type: "text", heading: "What the wallet is telling you", body: "A note passes through several states and they are not interchangeable. Pending means submitted but not yet in a block. Ready means it is in the tree and spendable. Spending means the payment is sent but not yet in a block; settling means it is in a block this node holds, but the committee has not finalized it yet, so the note is gone while the money has not provably arrived. Spent means finalized and genuinely delivered. Cancelled means you took it back before it landed. Returned means it never landed and never will." },
      { type: "text", heading: "Delivered means the network agreed", body: "A note only reads as spent once the committee has finalized the block holding it — not merely because your own node has that block. A block below the finalized height can still be reorganised away, and telling you your money arrived while the recipient has never seen it is the worst thing a wallet can do: you believe you have paid, and you have not. Ego would rather show you a payment still settling than a payment that has not happened." },
      { type: "text", heading: "Coins are never lost to a payment that did not happen", body: "The same promise runs the other way. A deposit that never reaches a block ends as returned with your balance untouched, because the transfer never executed — there is nothing to recover, it was never taken. A withdrawal that never lands puts the note back to ready and it can be spent again. Losing your network in the middle of either is not a way to lose money. And neither decision is made while your node is still catching up: a node that has not applied the block carrying your transaction cannot tell never included from not seen yet, so it waits rather than guessing." },
      { type: "text", heading: "Be clear about what it does not hide", body: "Amounts are visible on both the deposit and the payment — only the link between them is hidden. Your cover is everyone else holding notes of the size you are spending, so a pool with few users offers little of it, and withdrawing moments after depositing can still be matched by timing. Large holders are the most exposed, because fewest others hold the biggest notes. Waiting, splitting across addresses and dates, and using sizes other people also hold all help. Shielded-to-shielded transfers, which would hide amounts too and remove the need for denominations, are not built yet." },
      { type: "text", heading: "A testnet preview, and labelled as one", body: "The pool carries a TESTNET PREVIEW badge in the app because that is exactly what it is. The circuit has had no external security review, and an unaudited privacy circuit is not a thing to put real savings behind — a flaw in it would not merely leak, it could let coins be created. Use it on the testnet, tell us what breaks, and wait for the audit before trusting it with anything you cannot afford to lose. An independent audit is funded by the current round." },
    ],
  },
  {
    slug: "messenger",
    tag: "Messenger",
    title: "Messenger: Private, Peer-to-Peer Chat",
    date: "June 2026",
    excerpt:
      "End-to-end encrypted messaging between wallets — plus a built-in AI assistant.",
    hero: "/Messenger/chat.png",
    sections: [
      { type: "text", body: "Ego Messenger is private chat built on your wallet identity. Messages are end-to-end encrypted, so only you and your contact can read them — no server in the middle with the keys." },
      { type: "image", src: "/Messenger/add-contact.png", caption: "Add a contact by exchanging secure pairing cards." },
      { type: "text", heading: "How pairing works", body: "You and a contact swap encrypted contact bundles once. From then on, every message is encrypted with a shared key that only the two of you hold — even file shares and keys travel privately." },
      { type: "image", src: "/Messenger/ego-ai.png", caption: "Chat with the built-in Ego AI assistant right inside Messenger." },
    ],
  },
  {
    slug: "governance",
    tag: "Governance",
    title: "Governance: A DAO Anyone Can Join",
    date: "June 2026",
    excerpt:
      "Propose changes and vote with stake and knowledge — the community steers the network together.",
    hero: "/Governance/Proposal.png",
    sections: [
      { type: "text", body: "Ego is governed by its community, not a company. Anyone can submit a proposal, and the network decides together through a transparent, on-app process." },
      { type: "text", heading: "Creating a proposal", body: "Draft a proposal with a title, description, and options. To keep things fair and spam-free, each member can create a limited number of proposals per month." },
      { type: "image", src: "/Governance/vote.png", caption: "Members vote on live proposals — stake-weighted and knowledge-tested." },
      { type: "text", heading: "Two kinds of votes", body: "Ego uses dual voting: one vote weighted by stake, and one earned by passing a short knowledge test about the proposal. That rewards both commitment and understanding, so decisions reflect people who are invested and informed." },
    ],
  },
  {
    slug: "explorer",
    tag: "Explorer",
    title: "The Explorer: See the Chain in Real Time",
    date: "June 2026",
    excerpt:
      "Browse recent blocks and transactions and inspect any detail — straight from your own node.",
    hero: "/Explorer/explorer.png",
    sections: [
      { type: "text", body: "The Explorer gives you a live window into the chain. Because Ego Desktop is a full node, it shows the recent blocks and transactions your machine has synced — no third-party explorer required." },
      { type: "text", heading: "Inspecting a transaction", body: "Click any transaction to see its full details: amounts, addresses, block height, and timestamp. It's the same transparency a public block explorer offers, served from your own node." },
      { type: "image", src: "/Explorer/transaction-details.png", caption: "Drill into any transaction to see its complete on-chain details." },
    ],
  },
];

/* ── Comment system (per-article, persisted locally) ───────────────────────── */
function CommentSection({ slug }) {
  const storeKey = `ego-blog-comments-${slug}`;
  const likedKey = "ego-blog-liked";
  const nameKey = "ego-blog-username";

  const [comments, setComments] = useState([]);
  const [liked, setLiked] = useState({});
  const [username, setUsername] = useState("");
  const [text, setText] = useState("");
  const [clientId, setClientId] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");

  useEffect(() => {
    try {
      setComments(JSON.parse(localStorage.getItem(storeKey) || "[]"));
      setLiked(JSON.parse(localStorage.getItem(likedKey) || "{}"));
      setUsername(localStorage.getItem(nameKey) || "");
      let id = localStorage.getItem("ego-blog-clientid");
      if (!id) {
        id = `c-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        localStorage.setItem("ego-blog-clientid", id);
      }
      setClientId(id);
    } catch (e) {
      /* ignore corrupt storage */
    }
  }, [storeKey]);

  function persist(next) {
    setComments(next);
    localStorage.setItem(storeKey, JSON.stringify(next));
  }

  function submit(e) {
    e.preventDefault();
    const name = username.trim();
    const body = text.trim();
    if (!name || !body) return;
    localStorage.setItem(nameKey, name);
    const comment = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      name,
      body,
      ts: Date.now(),
      likes: 0,
      owner: clientId,
    };
    persist([comment, ...comments]);
    setText("");
  }

  function startEdit(c) {
    setEditingId(c.id);
    setEditText(c.body);
  }
  function cancelEdit() {
    setEditingId(null);
    setEditText("");
  }
  function saveEdit(id) {
    const body = editText.trim();
    if (!body) return;
    persist(comments.map((c) => (c.id === id ? { ...c, body, edited: true } : c)));
    cancelEdit();
  }
  function deleteComment(id) {
    if (!window.confirm("Delete this comment? This can't be undone.")) return;
    if (editingId === id) cancelEdit();
    persist(comments.filter((c) => c.id !== id));
  }

  function toggleLike(id) {
    const isLiked = !!liked[id];
    const nextLiked = { ...liked };
    if (isLiked) delete nextLiked[id];
    else nextLiked[id] = true;
    setLiked(nextLiked);
    localStorage.setItem(likedKey, JSON.stringify(nextLiked));
    persist(
      comments.map((c) =>
        c.id === id ? { ...c, likes: Math.max(0, c.likes + (isLiked ? -1 : 1)) } : c
      )
    );
  }

  function fmt(ts) {
    const d = new Date(ts);
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  }

  return (
    <section className="comments">
      <h3>Comments ({comments.length})</h3>

      <form className="comment-form" onSubmit={submit}>
        <input
          type="text"
          placeholder="Your username"
          value={username}
          maxLength={32}
          onChange={(e) => setUsername(e.target.value)}
        />
        <textarea
          placeholder="Share your thoughts…"
          value={text}
          maxLength={1000}
          onChange={(e) => setText(e.target.value)}
        />
        <button className="comment-post" type="submit" disabled={!username.trim() || !text.trim()}>
          Post comment
        </button>
      </form>

      {comments.length === 0 ? (
        <p className="comments-empty">Be the first to comment.</p>
      ) : (
        <div className="comment-list">
          {comments.map((c) => {
            const mine = c.owner && c.owner === clientId;
            const isEditing = editingId === c.id;
            return (
              <div className="comment" key={c.id}>
                <div className="comment-top">
                  <div className="comment-avatar">{c.name.charAt(0).toUpperCase()}</div>
                  <span className="comment-name">{c.name}</span>
                  <span className="comment-date">
                    · {fmt(c.ts)}{c.edited ? " · edited" : ""}
                  </span>
                </div>

                {isEditing ? (
                  <div className="comment-edit">
                    <textarea
                      value={editText}
                      maxLength={1000}
                      onChange={(e) => setEditText(e.target.value)}
                    />
                    <div className="comment-edit-actions">
                      <button
                        className="comment-save"
                        onClick={() => saveEdit(c.id)}
                        disabled={!editText.trim()}
                        type="button"
                      >
                        Save
                      </button>
                      <button className="comment-cancel" onClick={cancelEdit} type="button">
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="comment-text">{c.body}</p>
                )}

                <div className="comment-actions">
                  <button
                    className={`comment-like ${liked[c.id] ? "liked" : ""}`}
                    onClick={() => toggleLike(c.id)}
                    type="button"
                  >
                    {liked[c.id] ? "♥" : "♡"} {c.likes} {c.likes === 1 ? "like" : "likes"}
                  </button>
                  {mine && !isEditing && (
                    <>
                      <button className="comment-action" onClick={() => startEdit(c)} type="button">
                        Edit
                      </button>
                      <button
                        className="comment-action danger"
                        onClick={() => deleteComment(c.id)}
                        type="button"
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

/* ── Shared navbar (matches the rest of the site) ──────────────────────────── */
function Nav() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  return (
    <>
      <header className="navbar relative z-10">
        <div className="logo">
          <Link to="/" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
            <img src="/ego.png" alt="Ego Blockchain Logo" className="logo-img" />
          </Link>
        </div>
        <button
          className={`hamburger ${menuOpen ? "active" : ""}`}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
        <nav className={`nav-links ${menuOpen ? "open" : ""}`}>
          <Link to="/download" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
            Download Ego Desktop
          </Link>
          <Link to="/blog" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
            Blog
          </Link>
          <Link to="/docs" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
            Docs
          </Link>
          <Link to="/egoscan" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
            EgoScan
          </Link>
          <Link to="/claim" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
            Claim EGOC
          </Link>
        </nav>
      </header>
      {menuOpen && <div className="menu-overlay" onClick={() => setMenuOpen(false)} />}
    </>
  );
}

/* ── Reusable article card ─────────────────────────────────────────────────── */
function ArticleCard({ a }) {
  return (
    <Link
      to={`/blog/${a.slug}`}
      className="blog-card"
      key={a.slug}
      onClick={() => window.scrollTo({ top: 0 })}
    >
      <img src={a.hero} alt={a.title} className="blog-card-img" />
      <div className="blog-card-body">
        <span className="blog-card-tag">{a.tag}</span>
        <h2>{a.title}</h2>
        <p>{a.excerpt}</p>
        <div className="blog-card-meta">
          <span>{a.date}</span>
          <span className="blog-card-more">Read more →</span>
        </div>
      </div>
    </Link>
  );
}

/* ── Related articles (suggested at the end of each post) ───────────────────── */
function RelatedArticles({ slug }) {
  const idx = ARTICLES.findIndex((a) => a.slug === slug);
  const others = [];
  for (let i = 1; i <= 3; i++) others.push(ARTICLES[(idx + i) % ARTICLES.length]);
  return (
    <section className="related">
      <h3>Keep reading</h3>
      <div className="blog-grid">
        {others.map((a) => (
          <ArticleCard a={a} key={a.slug} />
        ))}
      </div>
    </section>
  );
}

/* ── Article view ──────────────────────────────────────────────────────────── */
function Article({ article }) {
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [article.slug]);
  return (
    <div className="blog-wrap">
      <Link to="/blog" className="blog-back">← All articles</Link>
      <span className="article-tag">{article.tag}</span>
      <h1 className="article-title">{article.title}</h1>
      <p className="article-meta">{article.date}</p>
      <img
        src={article.hero}
        alt={article.title}
        className={`article-hero${article.heroVariant === "logo" ? " logo" : ""}`}
      />

      <div className="article-body">
        {article.sections.map((s, i) =>
          s.type === "image" ? (
            <figure className={`article-figure${s.variant === "logo" ? " logo" : ""}`} key={i}>
              <img src={s.src} alt={s.caption || ""} />
              {s.caption && <figcaption>{s.caption}</figcaption>}
            </figure>
          ) : (
            <div key={i}>
              {s.heading && <h2>{s.heading}</h2>}
              <p>{s.body}</p>
            </div>
          )
        )}
      </div>

      <RelatedArticles slug={article.slug} />
      <CommentSection slug={article.slug} />
    </div>
  );
}

/* ── Index view ────────────────────────────────────────────────────────────── */
function Index() {
  return (
    <div className="blog-wrap">
      <div className="blog-head">
        <h1>The Ego Blog</h1>
        <p>Stories, technology, and ideas behind the people-powered blockchain.</p>
      </div>
      <div className="blog-grid">
        {ARTICLES.map((a) => (
          <ArticleCard a={a} key={a.slug} />
        ))}
      </div>
    </div>
  );
}

export default function Blog() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const article = slug ? ARTICLES.find((a) => a.slug === slug) : null;

  useEffect(() => {
    if (slug && !article) navigate("/blog", { replace: true });
  }, [slug, article, navigate]);

  return (
    <>
      <BackgroundCanvas />
      <div className="blog-darken" />
      <Nav />
      {article ? <Article article={article} /> : <Index />}
      <MouseFooter />
    </>
  );
}
